import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import Autocomplete from '../../../../Shared/AutoCompleteInput/AutoCompleteInput.js';
import TextInput from '../../../../Shared/TextInput/TextInput';
import { setIsSubmit } from '../../saleJetBlockSlice';
import { showAppLoader, hideAppLoader } from '../../../../Shared/Loader/LoaderSlice';
import ProviderManager from '../../../../../provider/providerManager';
import { saveStateToHistoryAction } from '../../../../../utilities/stateHistory/stateHistorySlice';
import { validateValue } from '../../../../../utilities/validation/validator';
import { validationRules } from '../../../../../utilities/validation/validationVariables';
import Config from '../../../../../Config';
import { buildUrlAndOpenWindow } from '../../../../../utilities/StringHelpers/StringHelpers';
import './addressForm.scss';

const internetFormErrorMessages = {
  firstName: 'צריך להזין שם פרטי',
  lastName: 'צריך להזין שם משפחה',
  phoneNumber: 'צריך להזין מספר טלפון נייד',
  city: 'צריך לבחור עיר',
  street: 'צריך לבחור רחוב',
  home: 'צריך להזין מספר בית',
  apartment: 'צריך להזין מספר דירה',
};

const legacyFormErrorMessages = {
  firstName: 'נא להזין שם פרטי',
  lastName: 'נא להזין שם משפחה',
  phoneNumber: 'צריך להזין מספר טלפון נייד',
  city: 'יש לבחור יישוב',
  street: 'יש לבחור רחוב',
  home: 'יש לבחור מספר בית',
  apartment: 'יש להזין מספר דירה',
  apartmentInvalid: 'יש להזין מספר דירה תקין',
};

const apartmentNote = 'לבית פרטי הקלידו מספר דירה 1';

const AddressForm = (props) => {
  const formErrorMessages = props.isInternetFiberForm ? internetFormErrorMessages : legacyFormErrorMessages;
  const [cities, setCities] = useState([]);
  const [streets, setStreets] = useState([]);
  const [address, setAddress] = useState({
    city: { value: '', code: '', semel: '', error: '' },
    street: { value: '', code: '', semel: '', gisSemel: '', error: '' },
    home: { value: '', error: '' },
    floor: { value: '', error: '' },
    apartment: { value: '', error: '' },
    entry: { value: '', error: '' },
    zipCode: { value: '' },
    phoneNumber: { value: '', error: '' },
    firstName: { value: '', error: '' },
    lastName: { value: '', error: '' },
  });
  const [isCityWithoutStreets, setIsCityWithoutStreets] = useState({ value: false });
  const [currentFocus, setCurrentFocus] = useState({ value: 'city' });
  const cityRef = useRef();
  const streetRef = useRef();
  const homeRef = useRef();
  const apartmentRef = useRef();
  const floorRef = useRef();
  const phoneNumberRef = useRef();
  const firstNameRef = useRef();
  const lastNameRef = useRef();
  const [form, setForm] = useState({ cityName: props.selectedAddress?.cityName ?? '', streetName: props.selectedAddress?.streetName ?? '' });
  //isRequired is used to determine if the address form is required or not
  // if its false but user begin to fill the form, we need to set it to true
  const [isRequired, setIsRequired] = useState(props.isRequired ?? true);



  useEffect(() => {
    (async function getCities() {
      props.showAppLoader();
      try {
        const result = await ProviderManager.getProviderData('getCities', []);
        if (result.errorCode == undefined) {
          setCities(result.Body);
          setSelectedAddress(result.Body);
        }
      } catch (error) {
      } finally {
        props.hideAppLoader();
        props.saveStateToHistoryAction(`${window.location.pathname}::${window.location.hash}`);
      }
    })();
    return () => { };
  }, []);



  async function citySelectedChange(cityCode) {
    const selectedCity = cities.find((x) => x.cityCode === cityCode);
    if (selectedCity) {
      address.city = { value: selectedCity.cityName, code: cityCode, semel: selectedCity.citySemel, error: '' };
      address.street = { value: '', code: '', semel: '', gisSemel: '', error: '' };
      address.home = { value: '', error: '' };
      address.apartment = { value: '', error: '' };
      address.floor = { value: '', error: '' };
      address.entry = { value: '', error: '' };
      form.cityName = '';
      form.streetName = '';
      setForm({ ...form });
      setAddress({ ...address });
      setIsRequired(true);

      streetRef?.current?.onChange({ currentTarget: { value: '' } });
      getStreets(selectedCity.cityCode, selectedCity.cityName);
    } else {
      address.city = { value: '', code: '', semel: '', error: formErrorMessages.city };
      setAddress({ ...address });
    }
  }

  async function getStreets(cityCode, cityName) {
    const result = await ProviderManager.getProviderData('getStreetsByCity', [cityCode]);
    const streets = result.Body;
    setStreets(streets);

    if (streets.length == 0) {
      setIsCityWithoutStreets({ ...isCityWithoutStreets, value: true });
      address.street = { value: cityName, code: '9000', semel: '9000', error: '' };
      address.home = { value: '1', error: '' };
      address.apartment = { value: '1', error: '' };
      address.floor = { value: '0', error: '' };
      setAddress({ ...address });
    } else {
      setIsCityWithoutStreets({ ...isCityWithoutStreets, value: false });
      if (!props.selectedAddress || Object.keys(props.selectedAddress).length === 0) {
        setAddress({ ...address, street: { value: '', code: '', semel: '', error: '' } });
      }
      setCurrentFocus({ ...currentFocus, value: 'street' });
    }
  }

  async function streetSelectedChange(streetCode) {
    const selectedStreet = streets.find((x) => x.streetCode === streetCode);

    if (selectedStreet) {
      address.street = {
        value: selectedStreet.streetName,
        code: selectedStreet.streetCode,
        semel: selectedStreet.streetSemel,
        gisSemel: selectedStreet.streetGisSemel,
        error: '',
      };
      setCurrentFocus({ ...currentFocus, value: 'home' });
    } else {
      address.street = { value: '', code: '', semel: '', error: formErrorMessages.street };
    }

    if (!props.selectedAddress || Object.keys(props.selectedAddress).length === 0) {
      address.home = { value: '', error: '' };
      address.apartment = { value: '', error: '' };
      address.floor = { value: '', error: '' };
      address.entry = { value: '', error: '' };
    }

    setAddress({ ...address });
  }

  function homeChange(home) {
    if (home) {
      setAddress({ ...address, home: { value: home, error: '' } });
    } else {
      setAddress({ ...address, home: { value: home, error: formErrorMessages.home } });
    }
  }

  function apartmentChange(apartment) {
    if (apartment) {
      setAddress({ ...address, apartment: { value: apartment, error: '' } });
    } else {
      setAddress({ ...address, apartment: { value: apartment, error: formErrorMessages.apartment } });
    }
  }

  function floorChange(floor) {
    setAddress({ ...address, floor: { value: floor, error: '' } });
  }

  function getPhoneError(phoneNumber) {
    const trimmedPhone = phoneNumber?.trim() ?? '';
    if (props.isInternetFiberForm) {
      if (!trimmedPhone || validateValue(validationRules.phone, 'phone', trimmedPhone)) {
        return formErrorMessages.phoneNumber;
      }
      return '';
    }
    return validateValue(validationRules.phone, 'phone', trimmedPhone);
  }

  function phoneNumberChange(phoneNumber) {
    const phoneError = getPhoneError(phoneNumber);
    setAddress({ ...address, phoneNumber: { value: phoneNumber, error: phoneError } });
    if (phoneNumber?.trim()) {
      setIsRequired(true);
    }
  }

  function validateNameValue(value, emptyMessage) {
    const trimmedValue = value?.trim() ?? '';

    if (!trimmedValue || trimmedValue.length < 2) {
      return emptyMessage;
    }
    if (!/^[א-תA-Za-z ."״()-/\'/׳]+$/.test(trimmedValue)) {
      return emptyMessage;
    }
    return '';
  }

  function firstNameChange(firstName) {
    const firstNameError = validateNameValue(firstName, formErrorMessages.firstName);
    setAddress({ ...address, firstName: { value: firstName, error: firstNameError } });
    if (firstName?.trim()) {
      setIsRequired(true);
    }
  }

  function lastNameChange(lastName) {
    const lastNameError = validateNameValue(lastName, formErrorMessages.lastName);
    setAddress({ ...address, lastName: { value: lastName, error: lastNameError } });
    if (lastName?.trim()) {
      setIsRequired(true);
    }
  }

  function handleKeyDown(e, field) {
    if (e.key === 'Enter') {
      switch (field) {
        case 'firstName':
          if (props.displayLastName) {
            setCurrentFocus({ ...currentFocus, value: 'lastName' });
          } else if (props.displayPhoneNumber) {
            setCurrentFocus({ ...currentFocus, value: 'phoneNumber' });
          } else {
            setCurrentFocus({ ...currentFocus, value: 'city' });
          }
          break;
        case 'lastName':
          if (props.displayPhoneNumber) {
            setCurrentFocus({ ...currentFocus, value: 'phoneNumber' });
          } else {
            setCurrentFocus({ ...currentFocus, value: 'city' });
          }
          break;
        case 'home':
          setCurrentFocus({ ...currentFocus, value: 'floor' });
          break;
        case 'phoneNumber':
          setCurrentFocus({ ...currentFocus, value: 'city' });
          break;
        case 'floor':
          setCurrentFocus({ ...currentFocus, value: 'apartment' });
          break;
        case 'apartment':
          submit();
          break;
      }
    }
  }

  async function setSelectedAddress(citiesList) {
    setCurrentFocus({ ...currentFocus, value: 'city' });
    if (!props.selectedAddress || Object.keys(props.selectedAddress).length === 0) {
      return;
    }
    const selectedCity = citiesList.find((x) => x.cityName === props.selectedAddress.cityName);
    address.city = {
      value: selectedCity.cityName,
      code: selectedCity.cityCode,
      semel: selectedCity.citySemel,
      error: '',
    };
    address.street = {
      value: props.selectedAddress.streetName,
      code: props.selectedAddress.streetCode,
      semel: props.selectedAddress.streetSemel,
      gisSemel: props.selectedAddress.streetGisSemel,
      error: '',
    };
    address.home = { value: props.selectedAddress.home, error: '' };
    address.apartment = { value: props.selectedAddress.apartment, error: '' };
    address.floor = { value: props.selectedAddress.floor, error: '' };
    address.phoneNumber = { value: props.selectedAddress.phoneNumber ?? '', error: '' };
    address.firstName = { value: props.selectedAddress.firstName ?? '', error: '' };
    address.lastName = { value: props.selectedAddress.lastName ?? '', error: '' };
    setAddress({ ...address });
  }

  useEffect(() => {
    switch (currentFocus.value) {
      case 'street':
        if (streetRef.current) streetRef.current?.autoCompleteRef?.current?.focus();
        break;
      case 'home':
        if (homeRef.current) homeRef.current.focus();
        break;
      case 'phoneNumber':
        if (phoneNumberRef.current) phoneNumberRef.current.focus();
        break;
      case 'firstName':
        if (firstNameRef.current) firstNameRef.current.focus();
        break;
      case 'lastName':
        if (lastNameRef.current) lastNameRef.current.focus();
        break;
      case 'floor':
        if (floorRef.current) floorRef.current.focus();
        break;
      case 'apartment':
        if (apartmentRef.current) apartmentRef.current.focus();
        break;
    }
  }, [currentFocus]);

  useEffect(() => {
    if (props.isSubmit) {
      submit();
    }
  }, [props.isSubmit]);

  const checkIsValid = () => {
    let isValid = true;
    if (!address.city.value) {
      isValid = false;
      address.city.error = formErrorMessages.city;
      setAddress({ ...address });
    }
    if (!address.street.value) {
      isValid = false;
      address.street.error = formErrorMessages.street;
      setAddress({ ...address });
    }
    if (address.home.value == '') {
      isValid = false;
      address.home.error = formErrorMessages.home;
      setAddress({ ...address });
    }
    if (props.displayFirstName) {
      const trimmedFirstName = address.firstName.value?.trim() ?? '';
      const firstNameError = validateNameValue(trimmedFirstName, formErrorMessages.firstName);

      if (firstNameError) {
        isValid = false;
        address.firstName.error = firstNameError;
        setAddress({ ...address });
      }
    }
    if (props.displayLastName) {
      const trimmedLastName = address.lastName.value?.trim() ?? '';
      const lastNameError = validateNameValue(trimmedLastName, formErrorMessages.lastName);

      if (lastNameError) {
        isValid = false;
        address.lastName.error = lastNameError;
        setAddress({ ...address });
      }
    }
    if (props.displayPhoneNumber) {
      const phoneError = getPhoneError(address.phoneNumber.value);
      if (phoneError) {
        isValid = false;
        address.phoneNumber.error = phoneError;
        setAddress({ ...address });
      }
    }
    if (!props.withoutApartmentData) {
      if (address.apartment.value == '' || address.apartment.value < 0) {
        isValid = false;
        address.apartment.error = formErrorMessages.apartment;
        setAddress({ ...address });
      }
      if (address.apartment.value == '0') {
        isValid = false;
        address.apartment.error = props.isInternetFiberForm
          ? formErrorMessages.apartment
          : legacyFormErrorMessages.apartmentInvalid;
        setAddress({ ...address });
      }
    }
    return isValid;
  };

  async function submit() {
    props.showAppLoader();
    const isValid = checkIsValid();
    if (isValid) {
      if (address.street.semel) await getZipCode();
    }
    const response = setResponse(isValid);
    props.hideAppLoader();
    props.onSubmit(response);
  }

  async function getZipCode() {
    const zipCodeResult = await ProviderManager.getProviderData('getZipCode', [
      address.street.code,
      address.city.code,
      address.home.value,
      address.entry.value ? address.entry.value : 'no_entrance',
    ]);
    address.zipCode = { value: zipCodeResult.Body };
    setAddress({ ...address });
  }

  function openPrivacyPolicy(e) {
    e.preventDefault();
    const privacyPolicyLink = props.privacyPolicyLink || Config.cooperationPrivacyPolicyLink;
    if (privacyPolicyLink) {
      buildUrlAndOpenWindow(privacyPolicyLink);
    }
  }

  const setResponse = (isValid) => {
    let addressInfo = {};

    addressInfo.cityCode = address.city.code;
    addressInfo.citySemel = address.city.semel;
    addressInfo.cityName = address.city.value;
    addressInfo.streetCode = address.street.code;
    addressInfo.streetSemel = address.street.semel;
    addressInfo.streetGisSemel = address.street.gisSemel;
    addressInfo.streetName = address.street.value;
    addressInfo.home = address.home.value;
    addressInfo.floor = address.floor?.value?.trim() == '' ? '1' : address.floor.value;
    addressInfo.entry = ' ';
    addressInfo.apartment = address.apartment.value;
    addressInfo.zipCode = address.zipCode.value;
    addressInfo.phoneNumber = address.phoneNumber?.value;
    addressInfo.firstName = address.firstName?.value;
    addressInfo.lastName = address.lastName?.value;
    return { selectedAddress: addressInfo, isValid: isValid };
  };

  return (
    <div className={`address-sale-block${props.isInternetFiberForm ? ' address-sale-block--internet' : ''}`}>
      <div className='address-wrapper'>
        <div className='form-wrapper'>
          <div>
            {props.displayFirstName && (
              <div className='row-wrapper'>
                <TextInput
                  ref={firstNameRef}
                  value={address.firstName.value}
                  error={address.firstName.error}
                  placeholder='שם פרטי'
                  id='firstName'
                  name='firstName'
                  maxLength='50'
                  type='text'
                  autoComplete='given-name'
                  onChange={(e) => firstNameChange(e.target.value)}
                  required={isRequired ? 'required' : ''}
                  onKeyDown={(e) => handleKeyDown(e, 'firstName')}
                />
              </div>
            )}
            {props.displayLastName && (
              <div className='row-wrapper'>
                <TextInput
                  ref={lastNameRef}
                  value={address.lastName.value}
                  error={address.lastName.error}
                  placeholder='שם משפחה'
                  id='lastName'
                  name='lastName'
                  maxLength='50'
                  type='text'
                  autoComplete='family-name'
                  onChange={(e) => lastNameChange(e.target.value)}
                  required={isRequired ? 'required' : ''}
                  onKeyDown={(e) => handleKeyDown(e, 'lastName')}
                />
              </div>
            )}
            {props.displayPhoneNumber && (
              <div className='row-wrapper'>
                <TextInput
                  ref={phoneNumberRef}
                  value={address.phoneNumber.value}
                  error={address.phoneNumber.error}
                  placeholder='מספר טלפון'
                  id='phoneNumber'
                  name='phoneNumber'
                  maxLength='10'
                  type='tel'
                  pattern='[0-9]*'
                  autoComplete='tel'
                  onChange={(e) => phoneNumberChange(e.target.value)}
                  required={isRequired ? 'required' : ''}
                  onKeyDown={(e) => handleKeyDown(e, 'phoneNumber')}
                />
              </div>
            )}
            <div className='row-wrapper'>
              <Autocomplete
                ref={cityRef}
                injectedValue={props.selectedAddress?.cityName ?? ''}
                form={form}
                error={address.city.error}
                value={address.city.value}
                id='city'
                name='city'
                onSelectedChange={(e) => citySelectedChange(e.target.value)}
                suggestions={cities}
                dataKey='cityCode'
                dataValue='cityName'
                placeholder='שם יישוב'
                required={isRequired ? 'required' : ''}
              />
            </div>

            <div className='row-wrapper'>
              {isCityWithoutStreets.value ? (
                <TextInput
                  value={address.street.value}
                  placeholder='רחוב'
                  id='dummyStreet'
                  name='dummyStreet'
                  type='text'
                  disabled={'disabled'}
                  maxLength='10'
                  autoComplete='off'
                  required={isRequired ? 'required' : ''}
                />
              ) : (
                <Autocomplete
                  ref={streetRef}
                  injectedValue={address.street.value ?? ''}
                  form={form}
                  error={address.street.error}
                  value={address.street.value}
                  id='street'
                  name='street'
                  disabled={address.city.value != '' ? '' : 'disabled'}
                  onSelectedChange={(e) => streetSelectedChange(e.target.value)}
                  suggestions={streets}
                  dataKey='streetCode'
                  dataValue='streetName'
                  placeholder='רחוב'
                  required={isRequired ? 'required' : ''}
                />
              )}
            </div>

            {!props.withoutApartmentData && !props.hideFloor ? (
              <>
                <div className='row-wrapper'>
                  <TextInput
                    ref={homeRef}
                    value={address.home.value}
                    error={address.home.error}
                    placeholder='מספר בית'
                    id='home'
                    name='home'
                    maxLength='4'
                    disabled={!isCityWithoutStreets.value && address.street?.value != '' ? '' : 'disabled'}
                    type='number'
                    pattern='[0-9]*'
                    autoComplete='off'
                    onChange={(e) => homeChange(e.target.value)}
                    required={isRequired ? 'required' : ''}
                    onKeyDown={(e) => handleKeyDown(e, 'home')}
                  />
                </div>
                <div className='row-wrapper multiple-fields'>
                  <div>
                    <TextInput
                      ref={floorRef}
                      value={address.floor.value}
                      error={address.floor.error}
                      placeholder='קומה'
                      id='floor'
                      name='floor'
                      maxLength='4'
                      disabled={!isCityWithoutStreets.value && address.street?.value != '' ? '' : 'disabled'}
                      type='number'
                      pattern='[0-9]*'
                      autoComplete='off'
                      onChange={(e) => floorChange(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, 'floor')}
                    />
                  </div>
                  <div>
                    <TextInput
                      ref={apartmentRef}
                      value={address.apartment.value}
                      error={address.apartment.error}
                      placeholder='דירה'
                      id='apartment'
                      name='apartment'
                      maxLength='4'
                      disabled={!isCityWithoutStreets.value && address.street?.value != '' ? '' : 'disabled'}
                      type='number'
                      pattern='[0-9]*'
                      autoComplete='off'
                      onChange={(e) => apartmentChange(e.target.value)}
                      required={isRequired ? 'required' : ''}
                      onKeyDown={(e) => handleKeyDown(e, 'apartment')}
                    />
                  </div>
                </div>
                {props.isInternetFiberForm && (
                  <div className='row-wrapper row-wrapper--apartment-note'>
                    <div className='apartment-message'>{apartmentNote}</div>
                  </div>
                )}
              </>
            ) : !props.withoutApartmentData && props.hideFloor ? (
              <>
                <div className='row-wrapper multiple-fields'>
                  <div>
                    <TextInput
                      ref={homeRef}
                      value={address.home.value}
                      error={address.home.error}
                      placeholder='מספר בית'
                      id='home'
                      name='home'
                      maxLength='4'
                      disabled={!isCityWithoutStreets.value && address.street?.value != '' ? '' : 'disabled'}
                      type='number'
                      pattern='[0-9]*'
                      autoComplete='off'
                      onChange={(e) => homeChange(e.target.value)}
                      required={isRequired ? 'required' : ''}
                      onKeyDown={(e) => handleKeyDown(e, 'home')}
                    />
                  </div>
                  <div>
                    <TextInput
                      ref={apartmentRef}
                      value={address.apartment.value}
                      error={address.apartment.error}
                      placeholder='דירה'
                      id='apartment'
                      name='apartment'
                      maxLength='4'
                      disabled={!isCityWithoutStreets.value && address.street?.value != '' ? '' : 'disabled'}
                      type='number'
                      pattern='[0-9]*'
                      autoComplete='off'
                      onChange={(e) => apartmentChange(e.target.value)}
                      required={isRequired ? 'required' : ''}
                      onKeyDown={(e) => handleKeyDown(e, 'apartment')}
                    />
                  </div>
                </div>
                {props.isInternetFiberForm && (
                  <div className='row-wrapper row-wrapper--apartment-note'>
                    <div className='apartment-message'>{apartmentNote}</div>
                  </div>
                )}
              </>
            ) : (
              <div className='row-wrapper'>
                <TextInput
                  ref={homeRef}
                  value={address.home.value}
                  error={address.home.error}
                  placeholder='מספר בית'
                  id='home'
                  name='home'
                  maxLength='4'
                  disabled={!isCityWithoutStreets.value && address.street?.value != '' ? '' : 'disabled'}
                  type='number'
                  pattern='[0-9]*'
                  autoComplete='off'
                  onChange={(e) => homeChange(e.target.value)}
                  required={isRequired ? 'required' : ''}
                  onKeyDown={(e) => handleKeyDown(e, 'home')}
                />
              </div>
            )}
            {props.displayPrivacyNotice && (
              <div className='row-wrapper row-wrapper--privacy-notice'>
                <p className='privacy-notice'>
                  הפרטים יאספו למאגרי המידע שלנו. אינך חייב למסור את המידע, אך ככל שלא תמסור אותו, ייתכן שלא נוכל לספק לך שירות. למידע מפורט, לרבות בדבר פרטינו, המידע האישי הנאסף, מטרות האיסוף, למי המידע עשוי להימסר, וזכותך לעיין במידע ולתקנו, ראו{' '}
                  <a href='#' onClick={openPrivacyPolicy}>
                    מדיניות הפרטיות
                  </a>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = {
  setIsSubmit,
  showAppLoader,
  hideAppLoader,
  saveStateToHistoryAction
};

export default connect(mapStateToProps, mapDispatchToProps)(AddressForm);
